// 영상 → 움직이는(애니메이션) WebP 변환 + 카페24 업로드 공용 유틸
// reviewWrite.js 의 인라인 로직을 추출 — 리뷰 작성/숏폼위젯에서 공용으로 사용한다.
import { createFFmpeg, fetchFile } from "@ffmpeg/ffmpeg";

let _ffmpeg = null;

// ffmpeg.wasm 싱글톤 (≈25MB WASM 1회 로드 후 재사용 — 여러 영상 순차 변환에 유리)
async function getFFmpeg() {
  if (!_ffmpeg) {
    _ffmpeg = createFFmpeg({ log: false });
  }
  if (!_ffmpeg.isLoaded()) {
    await _ffmpeg.load();
  }
  return _ffmpeg;
}

/**
 * 영상 파일 → 움직이는 WebP File 로 변환 (무한 루프 애니메이션)
 * @param {File} file 원본 영상
 * @param {string} outName 확장자 제외 출력 파일명
 * @param {object} [opts] { fps=10, width=360 }
 * @returns {Promise<File>} image/webp File
 */
export async function convertVideoToAnimatedWebp(file, outName, opts = {}) {
  const fps = opts.fps || 10;
  const width = opts.width || 360;
  const ffmpeg = await getFFmpeg();

  const inputName = "input_" + Date.now() + "." + (file.name.split(".").pop() || "mp4");
  const outputName = "output_" + Date.now() + ".webp"; // 동시/연속 변환 충돌 방지

  ffmpeg.FS("writeFile", inputName, await fetchFile(file));
  await ffmpeg.run(
    "-i", inputName,
    "-vf", `fps=${fps},scale=${width}:-1`,
    "-loop", "0",
    outputName
  );
  const data = ffmpeg.FS("readFile", outputName);

  // 가상 파일시스템 정리
  try { ffmpeg.FS("unlink", inputName); } catch (e) {}
  try { ffmpeg.FS("unlink", outputName); } catch (e) {}

  const blob = new Blob([data.buffer], { type: "image/webp" });
  return new File([blob], (outName || "animated") + ".webp", { type: "image/webp" });
}

/**
 * 파일을 임시 업로드 서버(/api/upload) → 카페24 파일 업로드 → 카페24 CDN URL 반환
 * @param {File} file 업로드할 파일(webp 등)
 * @param {object} ctx { storeId, cafe24UploadFiles } — cafe24UploadFiles 는 주입된 store action
 * @returns {Promise<{url:string}>}
 */
export async function uploadFileToCafe24(file, ctx) {
  const { storeId, cafe24UploadFiles } = ctx || {};
  const fetchHeader = process.env.NODE_ENV === "development" ? { 'X-Target-Server': '' } : {};

  const formData = new FormData();
  formData.append("file", file);

  let response;
  try {
    response = await fetch('https://orpapi.onreview.co.kr/api/upload', {
      method: 'POST',
      body: formData,
      headers: fetchHeader
    });
  } catch (e) {
    throw new Error(`업로드 서버 연결 실패: ${e.message}`);
  }
  if (!response.ok) {
    throw new Error(`업로드 서버 오류 (HTTP ${response.status})`);
  }

  let data;
  try {
    data = await response.json();
  } catch (e) {
    throw new Error('업로드 응답을 읽을 수 없습니다.');
  }
  if (!Array.isArray(data) || !data[0] || !data[0].fullPath) {
    console.error('업로드 응답 비정상:', data);
    throw new Error('업로드 응답이 올바르지 않습니다.');
  }

  return await new Promise((resolve, reject) => {
    cafe24UploadFiles({ store_id: storeId, imagesUrls: [data[0].fullPath] }, function (res) {
      if (res && res.errors && res.errors.length > 0) {
        return reject(new Error(res.errors[0].message || '카페24 파일 업로드에 실패했습니다.'));
      }
      if (res && res.error && res.error.message) {
        return reject(new Error(`카페24: ${res.error.message}`));
      }
      if (res && Array.isArray(res.errors) && res.errors[0]) {
        const e0 = res.errors[0];
        return reject(new Error(`카페24: ${e0.message || e0.code || '파일 업로드 실패'}`));
      }
      if (res && res.message && !res.files) {
        return reject(new Error(`카페24: ${res.message}`));
      }
      if (res && Array.isArray(res.files) && res.files[0]) {
        const f = res.files[0];
        const url = f.url || f.path;
        if (!url || !/^https?:\/\//.test(url)) {
          console.error('카페24 반환 URL 비정상:', f);
          return reject(new Error('카페24가 반환한 이미지 URL이 유효하지 않습니다.'));
        }
        return resolve({ ...f, url });
      }
      console.error('카페24 응답 비정상:', res);
      reject(new Error('카페24 업로드 응답이 비어 있거나 형식이 올바르지 않습니다.'));
    });
  });
}
